import { Component, OnInit } from '@angular/core';
import { faCircleQuestion, faComments } from '@fortawesome/pro-solid-svg-icons';
import { catchError, of, takeUntil, tap } from 'rxjs';

import { BaseComponent, DialogComponent, DialogService } from '@sircon/sircon-ui-framework';

import { AuthenticationService } from 'src/services/authentication.service';
import { EnvService } from 'src/services/env.service';
import { HelpService } from 'src/services/help.service';
import { UserContextService } from 'src/services/user-context.service';
import { UserContext } from 'src/app/core/models/user/user-context.model';
import { UiLoggingHttpService } from 'src/providers/ui-logging-http.service';
import { UILogRequest } from 'src/app/core/models/edge/ui-log-request.model';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TermsOfServiceComponent } from '../terms-of-service/terms-of-service.component';
import {
    AccountSecurityUpdateComponent
} from '../account-security/account-security-update/account-security-update.component';
import { DatePipe } from '@angular/common';

declare global {
    interface Window {
        _laq: any,
        liveagent: any
    }
}

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.less'],
    imports: [FaIconComponent, DialogComponent, TermsOfServiceComponent, AccountSecurityUpdateComponent, DatePipe]
})
export class FooterComponent extends BaseComponent implements OnInit {
    readonly applicableLoginSources: string[] = ['sirconwebsite', 'sirconIndustryPlatform'];

    date = new Date();
    liveChatId: string;
    liveChatInitiliazed: boolean;
    stateInfoCenterUrl: string;
    vertaforeUrl: string;

    userId: string;
    primaryEmailAddress: string;
    backupEmailAddress: string;
    showSecurityQuestions: boolean;
    showBackupEmail: boolean;

    faCircleQuestion = faCircleQuestion;
    faComments = faComments;

    constructor(
        private authenticationService: AuthenticationService,
        private dialogService: DialogService,
        private env: EnvService,
        private helpService: HelpService,
        private userContextService: UserContextService,
        private uiLoggingService: UiLoggingHttpService,
        private window: Window
    ) {
        super();
        this.liveChatId = this.env.FOOTER_LIVE_CHAT_BUTTON_ID;
        this.stateInfoCenterUrl = this.env.FOOTER_STATE_INFO_CENTER_URL;
        this.vertaforeUrl = this.env.FOOTER_VERTAFORE_URL;
    }

    ngOnInit(): void {
        // Live chat setup handled by userContext subscription and initial call
        this.userContextService.context$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                if (!this.liveChatInitiliazed) {
                    this.setupLiveChat();
                }
            });
        this.setupLiveChat();

        this.determineAccountRecoveryPrompt();
    }

    openDialog(dialogId: string): void {
        this.dialogService.open(dialogId);
    }

    helpUrl() {
        return this.helpService.getHelpUrlPath();
    }

    isHelpLinkAvailable(): boolean {
        return this.authenticationService.isAuthenticated() && this.userContextService.hasPermissionToViewComponent('helpLink');
    }

    /**
     * Tours are currently disabled until XD redesigns them
     *
     * Previous implementation:
     * this.authenticationService.isAuthenticated() && this.userContextService.hasPermissionToViewComponent('siteTour')
     */
    isTourAvailable(): boolean {
        return false;
    }

    isLiveChatAvailable(): boolean {
        return this.authenticationService.isAuthenticated() && this.userContextService.hasPermissionToViewComponent('liveChat');
    }

    isStateInfoCenterAvailable(): boolean {
        return this.authenticationService.isAuthenticated() && this.userContextService.hasPermissionToViewComponent('stateInfoCenter');
    }

    setupLiveChat(): void {
        // Using an imported library - see
        // https://developer.salesforce.com/docs/atlas.en-us.live_agent_dev.meta/live_agent_dev/live_agent_dev_guide_intro.htm
        if (this.isLiveChatAvailable()) {
            if (!this.window._laq) {
                this.window._laq = [];
            }
            // Never leave this on, but it is a way to get the live agent initialization/behavior in the console
            // this.window.liveagent.enableLogging();
            this.window._laq.push(() => this.setupLiveChatLaq());
            this.window.liveagent.init(this.env.FOOTER_LIVE_CHAT_URL, this.env.FOOTER_LIVE_CHAT_DEPLOYMENT_ID,
                this.env.FOOTER_LIVE_CHAT_ORGANIZATION_ID);
            this.liveChatInitiliazed = true;
        }
    }

    setupLiveChatLaq(): void {
        this.window.liveagent.showWhenOnline(this.liveChatId, document.getElementById('live-chat-online'));
        this.window.liveagent.showWhenOffline(this.liveChatId, document.getElementById('live-chat-offline'));
    }

    startChat(): void {
        this.window.liveagent.startChat(this.liveChatId);
    }

    /**
     * Determines if the Terms of Service link should be shown in the footer.  This starts off as false because it is
     * not allowed for public (unauthenticated) users and will be true after the user logs in.  However, if this is a property,
     * it will not have an associated watcher and the footer will never repaint.  Therefore, this has to be a function.
     *
     * @returns If the user has the viewTermsOfService permission.
     */
    showTermsOfService(): boolean {
        return this.userContextService.isAuthorizedForFunction('viewTermsOfService');
    }

    /**
     * Determines if the Early Adopter Program terms link should be shown in the footer.
     *
     * @returns {*|Boolean} If user is a Carrier user and the environment is not PROD.
     */
    showEarlyAdopterProgramTerms(): boolean {
        return this.userContextService.isCarrierAccount() && this.env.KEY !== 'PROD' && this.env.KEY !== 'MDC';
    }

    openAccountSecurityUpdateDialog(): void {
        this.dialogService.open('account-security-update');
    }

    closeAccountSecurityUpdateDialog(): void {
        this.dialogService.close('account-security-update');
    }

    stateChangeEventListener(/* event, toState, toParams, fromState, fromParams*/): void {
        if (!this.liveChatInitiliazed) {
            this.setupLiveChat();
        }
        this.dialogService.close('tour-dialog');
        this.dialogService.close('terms-of-service');
        this.closeAccountSecurityUpdateDialog();
    }

    determineAccountRecoveryPrompt() {
        this.authenticationService.userAuthenticated.pipe(
            tap((ctx: UserContext): void => {
                const loginSource: string = this.authenticationService.getJwtLoginSource();
                const accountType: string = ctx.account.accountType;

                const shouldCheckRecoverySetup: boolean =
                    this.applicableLoginSources.includes(loginSource) && accountType === 'individual';
                if (shouldCheckRecoverySetup) {
                    this.showSecurityQuestions = !ctx.user.accountRecoveryAdded;
                    this.showBackupEmail = !ctx.user.backupEmailAddressVerified;
                    this.userId = ctx.user.id;
                    this.primaryEmailAddress = ctx.user.emailAddress;
                    // Show the dialog if security questions are missing.  (If back-up email is the only thing missing, no dialog.)
                    if (this.showSecurityQuestions) {
                        this.openAccountSecurityUpdateDialog();
                    }
                }
            }),
            catchError(() => {
                const logRequest: UILogRequest = {
                    level: 'warn',
                    message: 'Unable to load data for account security update.  User was not prompted'
                };
                this.uiLoggingService.log(logRequest)
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe();
                // If Logging fails, let it silently fail because it is just logging
                return of();
            }),
            takeUntil(this.ngUnsubscribe)
        ).subscribe();
    }

    /**
     * This button allows user to quickly switch between test and local dev URL
     */
    isSwitchUrlAvailable(): boolean {
        return this.env.KEY === 'SIRCONTEST';
    }

    switchEnvUrl() {
        let newUrl: string;
        if (this.window.location.href.indexOf('platform.sircontest.non-prod.sircon.com') !== -1) {
            newUrl = this.window.location.href.replace('platform.sircontest.non-prod.sircon.com', 'devplatform.sircon.com:8080');
        } else if (this.window.location.href.indexOf('devplatform.sircon.com:8080') !== -1) {
            newUrl = this.window.location.href.replace('devplatform.sircon.com:8080', 'platform.sircontest.non-prod.sircon.com');
        }

        this.window.location.href = newUrl;
    }
}
